import React, { useState, useEffect } from 'react';
import { listAdmins, createAdmin, updateAdmin, deleteAdmin } from '../services/adminApi';
import './AdminManagement.css';

export default function AdminManagement() {
  const [activeTab, setActiveTab] = useState('list');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Create form state
  const [createEmail, setCreateEmail] = useState('');
  const [createName, setCreateName] = useState('');

  // Update form state
  const [updateEmail, setUpdateEmail] = useState('');
  const [updateName, setUpdateName] = useState('');
  const [updateStatus, setUpdateStatus] = useState('active');

  // Delete form state
  const [deleteEmail, setDeleteEmail] = useState('');

  useEffect(() => {
    if (activeTab === 'list') {
      loadAdmins();
    }
  }, [activeTab]);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const data = await listAdmins();
      setAdmins(data.admins || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      await createAdmin(createEmail, createName);
      setMessage(`Admin "${createEmail}" created successfully`);
      setCreateEmail('');
      setCreateName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      await updateAdmin(updateEmail, { full_name: updateName || undefined, status: updateStatus });
      setMessage(`Admin "${updateEmail}" updated successfully`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!window.confirm(`Are you sure you want to delete admin "${deleteEmail}"?`)) return;
    setLoading(true);
    try {
      await deleteAdmin(deleteEmail);
      setMessage(`Admin "${deleteEmail}" deleted successfully`);
      setDeleteEmail('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mgmt-content">
      <h2 className="page-title">Admin Management</h2>

      {/* Tab Navigation */}
      <div className="mgmt-tabs">
        <button className={`mgmt-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
          View Admins
        </button>
        <button className={`mgmt-tab ${activeTab === 'create' ? 'active' : ''}`} onClick={() => setActiveTab('create')}>
          Create Admin
        </button>
        <button className={`mgmt-tab ${activeTab === 'update' ? 'active' : ''}`} onClick={() => setActiveTab('update')}>
          Update Admin
        </button>
        <button className={`mgmt-tab ${activeTab === 'delete' ? 'active' : ''}`} onClick={() => setActiveTab('delete')}>
          Delete Admin
        </button>
      </div>

      {/* Messages */}
      {message && <div className="mgmt-success">{message}</div>}
      {error && <div className="mgmt-error">{error}</div>}

      {/* List Admins */}
      {activeTab === 'list' && (
        <div className="mgmt-card">
          <h3>All Admin Users</h3>
          {loading ? (
            <p className="mgmt-loading">Loading...</p>
          ) : admins.length === 0 ? (
            <p className="mgmt-empty">No admins found. Create one to get started.</p>
          ) : (
            <table className="mgmt-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Full Name</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.email}>
                    <td>{a.email}</td>
                    <td>{a.full_name}</td>
                    <td><span className={`status-badge status-${a.status || 'active'}`}>{a.status || 'active'}</span></td>
                    <td>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Admin */}
      {activeTab === 'create' && (
        <div className="mgmt-card">
          <h3>Create New Admin</h3>
          <form onSubmit={handleCreate} className="mgmt-form">
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={createEmail} onChange={e => setCreateEmail(e.target.value)} placeholder="admin@bt.com" required />
            </div>
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="John Smith" required />
            </div>
            <button type="submit" className="btn-action" disabled={loading}>
              {loading ? 'Creating...' : 'Create Admin'}
            </button>
          </form>
        </div>
      )}

      {/* Update Admin */}
      {activeTab === 'update' && (
        <div className="mgmt-card">
          <h3>Update Admin</h3>
          <form onSubmit={handleUpdate} className="mgmt-form">
            <div className="form-group">
              <label>Admin Email</label>
              <input type="email" value={updateEmail} onChange={e => setUpdateEmail(e.target.value)} placeholder="admin@bt.com" required />
            </div>
            <div className="form-group">
              <label>New Full Name</label>
              <input type="text" value={updateName} onChange={e => setUpdateName(e.target.value)} placeholder="Updated Name" />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={updateStatus} onChange={e => setUpdateStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button type="submit" className="btn-action" disabled={loading}>
              {loading ? 'Updating...' : 'Update Admin'}
            </button>
          </form>
        </div>
      )}

      {/* Delete Admin */}
      {activeTab === 'delete' && (
        <div className="mgmt-card">
          <h3>Delete Admin</h3>
          <form onSubmit={handleDelete} className="mgmt-form">
            <div className="form-group">
              <label>Admin Email to Delete</label>
              <input type="email" value={deleteEmail} onChange={e => setDeleteEmail(e.target.value)} placeholder="admin@bt.com" required />
            </div>
            <button type="submit" className="btn-action btn-danger" disabled={loading}>
              {loading ? 'Deleting...' : 'Delete Admin'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
